import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  Chip,
  Card,
  Button,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LinkIcon from '@mui/icons-material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import BackButton from '../../components/BackButton';
import { toast } from 'react-toastify';
import { fetchMeetingById } from '../../controllers/meetingsController';
import { formatTurkeyLocalDateTime } from '../../utils/turkeyLocalDateTime';
import { getCurrencyDisplayLabel } from '../../utils/currencyDisplay';
import {
  fetchMeetingAttendanceFullList,
  fetchMeetingAttendanceSummary,
  checkMeetingReportExists,
  generateMeetingAttendanceReport,
  fetchMeetingAttendanceReport,
  downloadMeetingAttendanceReportExcel,
} from '../../controllers/meetingsController';
import EditMeetingDialog from './EditMeetingDialog';
import MeetingAttendanceSummary from './components/MeetingAttendanceSummary';
import MeetingAttendanceTable from './components/MeetingAttendanceTable';
import MeetingReportDialog from './components/MeetingReportDialog';
import MeetingQRCodeDialog from './components/MeetingQRCodeDialog';
import EntityGalleryPanel from '../../components/EntityGalleryPanel';
import { GALLERY_ENTITY_TYPE } from '../../constants/galleryEntityTypes';
import { resolveGalleryAssetUrl } from '../../api/httpClientGalleries';

const MeetingDetail = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [reportExists, setReportExists] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportCheckDone, setReportCheckDone] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportDialogData, setReportDialogData] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  // Gallery

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchMeetingById(id);
        if (!active) return;
        setItem(data);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Toplantı alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let active = true;
    (async () => {
      setAttendanceLoading(true);
      try {
        const [fullList, summary, exists] = await Promise.all([
          fetchMeetingAttendanceFullList(id).catch(() => null),
          fetchMeetingAttendanceSummary(id).catch(() => null),
          checkMeetingReportExists(id).catch(() => false),
        ]);
        if (!active) return;
        setAttendanceData(fullList);
        setAttendanceSummary(summary);
        setReportExists(!!exists);
        setReportCheckDone(true);
      } catch (e) {
        if (!active) return;
        setAttendanceData(null);
        setAttendanceSummary(null);
        setReportCheckDone(true);
      } finally {
        if (active) setAttendanceLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  const loadAttendance = async () => {
    if (!id) return;
    setAttendanceLoading(true);
    try {
      const [fullList, summary, exists] = await Promise.all([
        fetchMeetingAttendanceFullList(id).catch(() => null),
        fetchMeetingAttendanceSummary(id).catch(() => null),
        checkMeetingReportExists(id).catch(() => false),
      ]);
      setAttendanceData(fullList);
      setAttendanceSummary(summary);
      setReportExists(!!exists);
    } catch (e) {
      setAttendanceData(null);
      setAttendanceSummary(null);
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!id) return;
    setReportLoading(true);
    try {
      await generateMeetingAttendanceReport(id);
      setReportExists(true);
      toast.success('Rapor oluşturuldu');
    } catch (e) {
      toast.error(e?.message || 'Rapor oluşturulamadı');
    } finally {
      setReportLoading(false);
    }
  };

  const handleViewReport = async () => {
    if (!id) return;
    setReportLoading(true);
    try {
      const data = await fetchMeetingAttendanceReport(id);
      setReportDialogData(data);
      setReportDialogOpen(true);
    } catch (e) {
      toast.error(e?.message || 'Rapor yüklenemedi');
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!id) return;
    setExcelLoading(true);
    try {
      const { blob, fileName, contentType } = await downloadMeetingAttendanceReportExcel(id);
      const url = URL.createObjectURL(new Blob([blob], { type: contentType }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || `MeetingAttendanceReport_${id}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.message || 'Excel indirilemedi');
    } finally {
      setExcelLoading(false);
    }
  };

  const formatDate = (dateString) => formatTurkeyLocalDateTime(dateString);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;
  if (!item) return null;

  return (
    <Stack spacing={2}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <BackButton />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>{item.title}</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={() => setEditOpen(true)}
            startIcon={<EditIcon sx={{ width: 16 }} />}
            sx={{
              textTransform: 'none',
              backgroundColor: '#E7EDF4',
              boxShadow: 'none',
              fontSize: 13,
              borderRadius: '5px',
              color: '#0E4D94',
              '&:hover': {
                backgroundColor: '#CFDBEA',
                boxShadow: 'none',
              },
            }}
          >
            Düzenle
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setQrOpen(true)}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              borderRadius: '5px',
            }}
          >
            QR Kodu Görüntüle
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 3fr' }, gap: 2 }}>
        {/* Sol Kart - Toplantı Bilgileri (1/4) */}
        <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px' }}>
          <Stack spacing={2}>
            {/* Tarih + Bitiş Tarihi */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <ScheduleIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>
                  Toplantı Tarihi
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                  {formatDate(item.meetingDate)}
                </Typography>
                {item.meetingEndDate && (
                  <>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25, mt: 1 }}>
                      Bitiş Tarihi
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                      {formatDate(item.meetingEndDate)}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Salon No */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <MeetingRoomIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>
                  Salon No
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                  {item.meetingRoomName || '-'}
                </Typography>
              </Box>
            </Box>

            {/* Durum */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {item.isActive ? (
                <CheckCircleIcon sx={{ fontSize: '20px', color: '#17C653', mt: 0.5, flexShrink: 0 }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: '20px', color: '#F8285A', mt: 0.5, flexShrink: 0 }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>
                  Durum
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  <Chip
                    label={item.isActive ? 'Aktif' : 'Pasif'}
                    size="small"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      backgroundColor: item.isActive ? '#DFFFEA' : '#FFEEF3',
                      color: item.isActive ? '#17C653' : '#F8285A',
                      height: 24,
                    }}
                  />
                  {item.isCompleted && (
                    <Chip
                      label="Tamamlandı"
                      size="small"
                      sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        backgroundColor: '#DFFFEA',
                        color: '#17C653',
                        height: 24,
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Box>

            {/* Lokasyon */}
            {item.location && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <LocationOnIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>
                    Lokasyon
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444' }}>
                    {item.location}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Yemek Ücreti */}
            {item.mealPrice && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <AttachMoneyIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>
                    Yemek Ücreti
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                    {item.mealPrice.toLocaleString('tr-TR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    {getCurrencyDisplayLabel(item.currency)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Misafir Bilgileri */}
            {item.allowGuests && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <EventAvailableIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>
                    Misafir
                  </Typography>
                  <Chip
                    label="Misafir Getirilebilir"
                    size="small"
                    sx={{ backgroundColor: '#E7EDF4', color: '#0E4D94', fontWeight: 600, fontSize: '12px', height: 24 }}
                  />
                </Box>
              </Box>
            )}

            {/* Online Toplantı Linki */}
            {item.onlineMeetingUrl && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <LinkIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>
                    Online Toplantı Linki
                  </Typography>
                  <Stack spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LaunchIcon />}
                      href={item.onlineMeetingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'none',
                        fontSize: 13,
                        borderRadius: '5px',
                      }}
                    >
                      Toplantıya Katıl
                    </Button>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', wordBreak: 'break-all' }}>
                      {item.onlineMeetingUrl}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            )}
          </Stack>
        </Card>

        {/* Sağ Kart - Açıklama (3/4) */}
        <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px' }}>
          <Stack spacing={3}>
            {/* Açıklama */}
            {item.description ? (
              <Box>
                <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', mb: 1.5 }}>
                  Açıklama
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '14px', color: '#444444' }}>
                  {item.description}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                  Açıklama bulunmuyor.
                </Typography>
              </Box>
            )}
          </Stack>
        </Card>
      </Box>

      {/* Attendance Summary */}
      {attendanceSummary && (
        <MeetingAttendanceSummary summary={attendanceSummary} />
      )}

      {/* Galeri */}
      {(item.coverImageUrl || item.CoverImageUrl) && (
        <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px' }}>
          <Box
            component="img"
            src={resolveGalleryAssetUrl(item.coverImageUrl || item.CoverImageUrl)}
            alt={item.title}
            sx={{
              width: '100%',
              maxHeight: 220,
              objectFit: 'cover',
              borderRadius: '8px',
              border: '1px solid #e6e6e6',
            }}
          />
        </Card>
      )}
      <EntityGalleryPanel
        relatedEntityType={GALLERY_ENTITY_TYPE.Meeting}
        relatedEntityId={id}
        initialGalleryId={item.galleryId || item.GalleryId}
        embeddedGallery={item.gallery || item.Gallery}
      />

      {/* Katılım Raporu - sadece Rapor Oluştur (rapor yoksa) */}
      {id && reportCheckDone && !reportExists && (
        <Card sx={{ p: 2.5, border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Stack direction="row" alignItems="center" gap={1}>
              <PictureAsPdfIcon sx={{ color: '#0E4D94', fontSize: 28 }} />
              <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                Katılım Raporu
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              startIcon={reportLoading ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon sx={{ width: 18 }} />}
              onClick={handleGenerateReport}
              disabled={reportLoading}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                borderRadius: '5px',
                borderColor: '#e6e6e6',
                color: '#444444',
                '&:hover': { borderColor: '#0E4D94', color: '#0E4D94', backgroundColor: 'rgba(14, 77, 148, 0.04)' },
              }}
            >
              Rapor Oluştur
            </Button>
          </Stack>
        </Card>
      )}

      {/* Attendance Table */}
      {id && (
        attendanceLoading && !attendanceData ? (
          <Card sx={{ p: 2.5, border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          </Card>
        ) : (
          <MeetingAttendanceTable
            meetingId={id}
            allowGuests={item?.allowGuests === true}
            attendanceData={attendanceData || { attendances: [], guestGroups: [] }}
            onRefresh={loadAttendance}
            reportExists={reportExists}
            reportLoading={reportLoading}
            excelLoading={excelLoading}
            onViewReport={handleViewReport}
            onDownloadExcel={handleDownloadExcel}
          />
        )
      )}

      {/* Edit Dialog */}
      {item && (
        <EditMeetingDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          meeting={item}
          onUpdated={(updated) => {
            setItem(updated);
            setEditOpen(false);
          }}
        />
      )}

      {/* Rapor Dialog */}
      <MeetingReportDialog
        open={reportDialogOpen}
        onClose={() => {
          setReportDialogOpen(false);
          setReportDialogData(null);
        }}
        data={reportDialogData}
      />

      {/* QR Kod Dialog */}
      {item && (
        <MeetingQRCodeDialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          meeting={item}
        />
      )}

    </Stack>
  );
};

export default MeetingDetail;
